import { Alert, Button, Flex, Heading, showAlertModalError } from '@conteg/ui';
import Notification from 'components/notification/notification';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  StorageUnitDocument,
  StorageUnitsDocument,
  useForcedOpenStorageUnitWithReservationMutation,
  useTerminateReservationMutation,
} from 'types/generated/graphql';
import { usePoint } from 'utils/device/device-store';
import { resolveTwoFactorErrorKey } from 'utils/errors/resolve-two-factor-error-key';
import { useInvalidateQueries } from 'utils/hooks/use-ivalidate-queries';

enum ModalStep {
  OPEN_STORAGE_UNIT = 'openStorageUnit',
  CANCEL_RESERVATION = 'cancelReservation',
  CONFIRM_CANCEL = 'confirmCancel',
}

const ForcedOpenUnitWithReservation = ({
  storageUnitId,
  reservationId,
  onClose,
  onDirectClose,
}: {
  storageUnitId: string;
  reservationId?: string | null;
  onClose?: () => void;
  onDirectClose?: () => void;
}) => {
  const { t } = useTranslation();
  const { pointId } = usePoint();
  const invalidate = useInvalidateQueries();

  const [formNotice, setFormNotice] = useState<string>('');
  const [currentStep, setCurrentStep] = useState<ModalStep>(
    ModalStep.OPEN_STORAGE_UNIT
  );

  const { mutate: openStorageUnitWithoutPin, isPending: isOpeningUnit } =
    useForcedOpenStorageUnitWithReservationMutation();

  const { mutate: terminateReservation, isPending: isCancellingReservation } =
    useTerminateReservationMutation();

  const handleSuccessOpenStorageUnit = () => {
    setFormNotice('Page.StorageUnit.Actions.Open.WithoutPin.Success');
    setCurrentStep(ModalStep.CANCEL_RESERVATION);
  };

  const handleOpenStorageUnit = () => {
    setFormNotice('');
    openStorageUnitWithoutPin(
      {
        pointId: pointId as string,
        storageUnitId,
      },
      {
        onSuccess: handleSuccessOpenStorageUnit,
        onError: (error) =>
          showAlertModalError(
            t(
              resolveTwoFactorErrorKey(
                error,
                'Page.StorageUnit.Actions.Open.WithoutPin.Error'
              )
            ),
            error
          ),
      }
    );
  };

  const handleTerminateReservation = () => {
    if (!reservationId) {
      showAlertModalError(
        t('Page.StorageUnit.Actions.Terminate.Reservation.Error'),
        new Error('Missing reservation id for terminate reservation')
      );
      return;
    }

    setFormNotice('');
    terminateReservation(
      {
        reservationId,
      },
      {
        onSuccess: () => {
          invalidate([StorageUnitDocument, StorageUnitsDocument]);
          onDirectClose?.();
        },
        onError: (error) =>
          showAlertModalError(
            t(
              resolveTwoFactorErrorKey(
                error,
                'Page.StorageUnit.Actions.Terminate.Reservation.Error'
              )
            ),
            error
          ),
      }
    );
  };

  if (currentStep === ModalStep.CONFIRM_CANCEL) {
    return (
      <Flex flexDirection="column" alignItems="center" gap="6rem" mt="5rem">
        <Heading
          variant="h2"
          title={t('Page.StorageUnit.Actions.Terminate.Reservation.Heading')}
        />
        <Alert
          type="warning"
          message={t('Page.StorageUnit.Actions.Terminate.Reservation.Heading')}
        />
        {formNotice && (
          <Notification
            testId="cancel-reservation-notification"
            message={formNotice}
          />
        )}
        <Flex gap="4rem" alignItems="center">
          <Button
            title={t('Kiosk.Layout.Modal.Button.Confirm')}
            testId="confirm-cancel-reservation-button"
            onClick={handleTerminateReservation}
            variant="danger"
            size="xl"
            disabled={isCancellingReservation}
          />
          <Button
            title={t('Kiosk.Layout.Modal.Button.Cancel')}
            testId="cancel-modal-button"
            onClick={() => onClose?.()}
            variant="secondary"
            size="xl"
            disabled={isCancellingReservation}
          />
        </Flex>
      </Flex>
    );
  }

  if (currentStep === ModalStep.CANCEL_RESERVATION) {
    return (
      <Flex flexDirection="column" alignItems="center" gap="6rem" mt="5rem">
        <Heading
          variant="h2"
          title={t('Page.StorageUnit.Actions.Terminate.Reservation')}
        />
        <Alert
          type="warning"
          message={t('Page.StorageUnit.Actions.Terminate.Reservation.Heading')}
        />
        {formNotice && (
          <Notification
            testId="cancel-reservation-notification"
            message={formNotice}
          />
        )}
        <Button
          title={t('Page.StorageUnit.Actions.Terminate.Reservation')}
          testId="cancel-reservation-button"
          onClick={() => setCurrentStep(ModalStep.CONFIRM_CANCEL)}
          variant="danger"
          size="xl"
          disabled={isCancellingReservation}
        />
      </Flex>
    );
  }

  return (
    <Flex flexDirection="column" alignItems="center" gap="6rem" mt="5rem">
      <Heading
        variant="h2"
        title={t('Page.StorageUnit.Actions.Open.ForcedStorageOpen.Title')}
      />
      <Alert
        type="warning"
        message={t(
          'Page.StorageUnit.Actions.Open.ForcedStorageOpen.Description'
        )}
      />
      {formNotice && (
        <Notification testId="resend-sms-notification" message={formNotice} />
      )}
      <Button
        title={t('Page.StorageUnit.Actions.Open.ForcedStorageOpen.Open')}
        testId="forced-storage-open-button"
        onClick={handleOpenStorageUnit}
        variant="primary"
        size="xl"
        disabled={isOpeningUnit}
      />
    </Flex>
  );
};

export default ForcedOpenUnitWithReservation;
